import { useSyncExternalStore } from 'react';
import { postavitZaKompyuterom } from './aktivnost';

/**
 * `s781` (владелец 11.10.2026: «менеджер был просто в других вкладках браузера, там ВКонтакте… Как-то
 * это реально отслеживать?»). Мышь и клавиши панель видит только в своих вкладках. Chrome, Яндекс
 * Браузер и Edge по разрешению человека говорят странице, трогал ли он мышь или клавиатуру где угодно
 * на компьютере и не заблокирован ли экран; что именно у него открыто, они не сообщают. Firefox,
 * Safari и телефоны этого не умеют — там остаётся правило вкладок панели.
 *
 *   net          — браузер не умеет;
 *   sprosit      — умеет, человека ещё не спрашивали;
 *   razresheno   — человек разрешил, панель слушает компьютер;
 *   zapreshcheno — человек запретил: вернуть можно только в настройках сайта в браузере.
 */
export type Prisutstvie = 'net' | 'sprosit' | 'razresheno' | 'zapreshcheno';

interface Detektor extends EventTarget {
  userState: 'active' | 'idle' | null;
  screenState: 'locked' | 'unlocked' | null;
  start(nastroyki: { threshold: number; signal?: AbortSignal }): Promise<void>;
}
interface DetektorKlass {
  new (): Detektor;
  requestPermission(): Promise<PermissionState>;
}

/** Меньше минуты тишины браузер не принимает. */
export const POROG_MS = 60_000;

/** null — ещё не выяснили. */
let sostoyanie: Prisutstvie | null = null;
let zapushcheno = false;
let nablyudenie: AbortController | null = null;
const slushateli = new Set<() => void>();

function klass(): DetektorKlass | null {
  return (window as unknown as { IdleDetector?: DetektorKlass }).IdleDetector ?? null;
}

function izRazresheniya(otvet: PermissionState): Prisutstvie {
  return otvet === 'granted' ? 'razresheno' : otvet === 'denied' ? 'zapreshcheno' : 'sprosit';
}

function smenit(novoe: Prisutstvie): void {
  if (sostoyanie === novoe) return;
  sostoyanie = novoe;
  slushateli.forEach((f) => f());
}

function ostanovit(): void {
  nablyudenie?.abort();
  nablyudenie = null;
  postavitZaKompyuterom(null);
}

async function nablyudat(): Promise<void> {
  const Klass = klass();
  if (!Klass || nablyudenie) return;
  const stop = new AbortController();
  nablyudenie = stop;
  try {
    const detektor = new Klass();
    const prochest = () => {
      if (stop.signal.aborted || detektor.userState === null) return;
      const zakryt = detektor.screenState === 'locked';
      // Экран заперли рукой — человек был здесь секунду назад; затих сам — уже минуту как.
      postavitZaKompyuterom(detektor.userState === 'active' && !zakryt, detektor.userState === 'active' ? 0 : POROG_MS);
    };
    detektor.addEventListener('change', prochest);
    await detektor.start({ threshold: POROG_MS, signal: stop.signal });
    prochest();
  } catch {
    // Браузер не дал слушать (разрешение сняли, страница во фрейме) — считаем по вкладкам панели.
    if (nablyudenie === stop) ostanovit();
  }
}

/** Выяснить, что умеет браузер и что уже решил человек; разрешено — слушать компьютер. Один раз на вкладку. */
export async function vklyuchitPrisutstvie(): Promise<void> {
  if (zapushcheno) return;
  zapushcheno = true;
  if (!klass()) { smenit('net'); return; }
  try {
    const razreshenie = await navigator.permissions.query({ name: 'idle-detection' as PermissionName });
    const primenit = () => {
      smenit(izRazresheniya(razreshenie.state));
      if (razreshenie.state === 'granted') void nablyudat(); else ostanovit();
    };
    razreshenie.addEventListener('change', primenit);
    primenit();
  } catch {
    smenit('net');
  }
}

/** Спросить у человека разрешение. Зовётся только из нажатия: без жеста браузер вопрос не покажет. */
export async function razreshitPrisutstvie(): Promise<void> {
  const Klass = klass();
  if (!Klass) return;
  try {
    const otvet = await Klass.requestPermission();
    smenit(izRazresheniya(otvet));
    if (otvet === 'granted') void nablyudat();
  } catch {
    /* вопрос не показан — остаётся правило вкладок панели */
  }
}

function podpisatsya(f: () => void): () => void {
  slushateli.add(f);
  return () => { slushateli.delete(f); };
}

export function usePrisutstvie(): Prisutstvie | null {
  return useSyncExternalStore(podpisatsya, () => sostoyanie);
}

/** Для проверок: забыть всё, что выяснили. */
export function sbrositPrisutstvie(): void {
  ostanovit();
  sostoyanie = null;
  zapushcheno = false;
}
